import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Select,
  Button,
  Progress,
  Tag,
  Modal,
  Form,
  InputNumber,
  Space,
  message,
  Tooltip,
  Alert,
  Empty
} from 'antd';
import {
  SoundOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
  SettingOutlined,
  WarningOutlined
} from '@ant-design/icons';
import { Line, Gauge } from '@ant-design/plots';
import { environmentService, NoiseData, StatsQuery } from '../../../services/environmentService';
import type { ColumnsType } from 'antd/es/table';

const { Option } = Select;

interface NoiseMonitoringProps {
  onRefresh?: () => void;
}

const NoiseMonitoring: React.FC<NoiseMonitoringProps> = ({ onRefresh }) => {
  const [loading, setLoading] = useState(false);
  const [noiseData, setNoiseData] = useState<NoiseData[]>([]);
  const [filteredData, setFilteredData] = useState<NoiseData[]>([]);
  const [statsData, setStatsData] = useState<any>(null);
  const [selectedSensor, setSelectedSensor] = useState<NoiseData | null>(null);
  const [thresholdModalVisible, setThresholdModalVisible] = useState(false);
  const [filters, setFilters] = useState<StatsQuery>({
    area: '',
    floor: undefined,
    period: 'daily'
  });
  const [form] = Form.useForm();

  // 載入噪音監測數據
  const loadNoiseData = async () => {
    try {
      setLoading(true);
      const [data, stats] = await Promise.all([
        environmentService.getNoiseData(filters),
        environmentService.getNoiseStats(filters)
      ]);
      setNoiseData(data);
      setFilteredData(data);
      setStatsData(stats);
    } catch (error) {
      console.error('載入噪音監測數據失敗:', error);
      message.error('載入噪音監測數據失敗');
    } finally {
      setLoading(false);
    }
  };

  // 設置噪音閾值
  const updateThresholds = async (values: any) => {
    if (!selectedSensor) return;

    try {
      await environmentService.setNoiseThresholds(selectedSensor.sensorId, values);
      message.success('噪音閾值設置成功');
      setThresholdModalVisible(false);
      setSelectedSensor(null);
      form.resetFields();
      await loadNoiseData();
      onRefresh?.();
    } catch (error) {
      console.error('設置噪音閾值失敗:', error);
      message.error('設置噪音閾值失敗');
    }
  };

  // 噪音控制處理
  const handleNoiseControl = async (sensorId: string, action: string) => {
    try {
      await environmentService.controlNoise(sensorId, action);
      message.success('噪音控制處理成功');
      await loadNoiseData();
      onRefresh?.();
    } catch (error) {
      console.error('噪音控制處理失敗:', error);
      message.error('噪音控制處理失敗');
    }
  };

  // 獲取噪音等級顏色和描述
  const getNoiseLevel = (decibel: number) => {
    if (decibel < 40) return { level: '安靜', color: '#52c41a', desc: '非常安靜的環境' };
    if (decibel < 55) return { level: '正常', color: '#faad14', desc: '正常的環境噪音' };
    if (decibel < 70) return { level: '吵雜', color: '#fa8c16', desc: '較為吵雜，需注意' };
    if (decibel < 85) return { level: '很吵', color: '#f5222d', desc: '很吵，影響工作' };
    return { level: '危險', color: '#722ed1', desc: '危險等級，需立即處理' };
  };

  // 獲取嚴重程度標籤
  const getSeverityTag = (severity: string) => {
    const config = {
      normal: { color: 'green', text: '正常' },
      warning: { color: 'orange', text: '警告' },
      critical: { color: 'red', text: '嚴重' }
    };
    const { color, text } = config[severity as keyof typeof config] || config.normal;
    return <Tag color={color}>{text}</Tag>;
  };

  // 表格列定義
  const columns: ColumnsType<NoiseData> = [
    {
      title: '感測器ID',
      dataIndex: 'sensorId',
      key: 'sensorId',
      width: 120
    },
    {
      title: '位置',
      key: 'location',
      width: 150,
      render: (_, record) => (
        <span>
          {record.location.area} - {record.location.floor}F
          {record.location.zone && ` - ${record.location.zone}`}
        </span>
      )
    },
    {
      title: '當前噪音 (dB)',
      key: 'current',
      width: 120,
      render: (_, record) => {
        const { level, color } = getNoiseLevel(record.measurements.decibel.current);
        return (
          <Tooltip title={`噪音等級: ${level}`}>
            <span style={{ color, fontWeight: 'bold' }}>
              {record.measurements.decibel.current} dB
            </span>
          </Tooltip>
        );
      },
      sorter: (a, b) => a.measurements.decibel.current - b.measurements.decibel.current
    },
    {
      title: '平均/峰值',
      key: 'avgPeak',
      width: 120,
      render: (_, record) => (
        <div>
          <div>平均: {record.measurements.decibel.average} dB</div>
          <div style={{ color: '#f5222d' }}>峰值: {record.measurements.decibel.peak} dB</div>
        </div>
      )
    },
    {
      title: '頻率分析',
      key: 'frequency',
      width: 150,
      render: (_, record) => (
        <div style={{ fontSize: 12 }}>
          <div>低頻: {record.measurements.frequency.low} Hz</div>
          <div>中頻: {record.measurements.frequency.mid} Hz</div>
          <div>高頻: {record.measurements.frequency.high} Hz</div>
        </div>
      )
    },
    {
      title: '狀態',
      key: 'severity',
      width: 80,
      render: (_, record) => getSeverityTag(record.classification.severity),
      filters: [
        { text: '正常', value: 'normal' },
        { text: '警告', value: 'warning' },
        { text: '嚴重', value: 'critical' }
      ],
      onFilter: (value, record) => record.classification.severity === value
    },
    {
      title: '閾值設置',
      key: 'thresholds',
      width: 120,
      render: (_, record) => (
        <div style={{ fontSize: 12 }}>
          <div>警告: {record.thresholds.warning} dB</div>
          <div>嚴重: {record.thresholds.critical} dB</div>
        </div>
      )
    },
    {
      title: '更新時間',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 160,
      render: (timestamp: string) => new Date(timestamp).toLocaleString('zh-TW'),
      sorter: (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
      defaultSortOrder: 'descend'
    },
    {
      title: '操作',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<SettingOutlined />}
            onClick={() => {
              setSelectedSensor(record);
              form.setFieldsValue({
                warning: record.thresholds.warning,
                critical: record.thresholds.critical
              });
              setThresholdModalVisible(true);
            }}
          >
            設置
          </Button>
          {record.classification.severity !== 'normal' && (
            <Button
              size="small"
              type="primary"
              onClick={() => handleNoiseControl(record.sensorId, 'reduce_noise')}
            >
              降噪
            </Button>
          )}
        </Space>
      )
    }
  ];

  // 準備圖表數據
  const prepareChartData = () => {
    if (!noiseData.length) return [];
    
    return noiseData.map(item => ({
      time: new Date(item.timestamp).toLocaleString('zh-TW', { 
        month: 'short', 
        day: 'numeric', 
        hour: '2-digit', 
        minute: '2-digit' 
      }),
      current: item.measurements.decibel.current,
      average: item.measurements.decibel.average,
      peak: item.measurements.decibel.peak,
      location: `${item.location.area}-${item.location.floor}F`
    }));
  };

  // 計算統計數據
  const calculateStats = () => {
    if (!noiseData.length) return null;

    const avgNoise = noiseData.reduce((sum, item) => sum + item.measurements.decibel.current, 0) / noiseData.length;
    const maxNoise = Math.max(...noiseData.map(item => item.measurements.decibel.current));
    const alertCount = noiseData.filter(item => item.classification.severity !== 'normal').length;
    const criticalCount = noiseData.filter(item => item.classification.severity === 'critical').length;

    return { avgNoise, maxNoise, alertCount, criticalCount };
  };

  useEffect(() => {
    loadNoiseData();
  }, [filters]);

  const stats = calculateStats();
  const avgNoiseInfo = stats ? getNoiseLevel(stats.avgNoise) : null;

  return (
    <div className="noise-monitoring">
      {/* 過濾器 */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col span={4}>
            <Select
              placeholder="選擇區域"
              value={filters.area}
              onChange={(value) => setFilters(prev => ({ ...prev, area: value }))}
              allowClear
              style={{ width: '100%' }}
            >
              <Option value="A區">A區</Option>
              <Option value="B區">B區</Option>
              <Option value="C區">C區</Option>
              <Option value="地下停車場">地下停車場</Option>
            </Select>
          </Col>
          <Col span={4}>
            <Select
              placeholder="選擇樓層"
              value={filters.floor}
              onChange={(value) => setFilters(prev => ({ ...prev, floor: value }))}
              allowClear
              style={{ width: '100%' }}
            >
              <Option value={-1}>B1</Option>
              <Option value={1}>1F</Option>
              <Option value={2}>2F</Option>
              <Option value={3}>3F</Option>
            </Select>
          </Col>
          <Col span={4}>
            <Select
              value={filters.period}
              onChange={(value) => setFilters(prev => ({ ...prev, period: value }))}
              style={{ width: '100%' }}
            >
              <Option value="hourly">每小時</Option>
              <Option value="daily">每日</Option>
              <Option value="weekly">每週</Option>
              <Option value="monthly">每月</Option>
            </Select>
          </Col>
          <Col span={8}>
            {stats && stats.criticalCount > 0 && (
              <Alert
                message={`檢測到 ${stats.criticalCount} 個嚴重噪音警報`}
                type="error"
                showIcon
                icon={<WarningOutlined />}
                style={{ margin: 0 }}
              />
            )}
          </Col>
          <Col span={4}>
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={loadNoiseData}
              loading={loading}
              style={{ width: '100%' }}
            >
              刷新
            </Button>
          </Col>
        </Row>
      </Card>

      {/* 統計概覽 */}
      {stats && (
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Card>
              <Statistic
                title="平均噪音等級"
                value={stats.avgNoise}
                precision={1}
                valueStyle={{ color: avgNoiseInfo?.color }}
                prefix={<SoundOutlined />}
                suffix="dB"
              />
              <div style={{ marginTop: 8 }}>
                <Tag color={avgNoiseInfo?.color}>{avgNoiseInfo?.level}</Tag>
                <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                  {avgNoiseInfo?.desc}
                </div>
              </div>
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="最高噪音"
                value={stats.maxNoise}
                precision={1}
                valueStyle={{ color: '#f5222d' }}
                suffix="dB"
              />
              <Progress
                percent={(stats.maxNoise / 100) * 100}
                strokeColor="#f5222d"
                showInfo={false}
                size="small"
                style={{ marginTop: 8 }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="警報數量"
                value={stats.alertCount}
                valueStyle={{ color: stats.alertCount > 0 ? '#fa8c16' : '#52c41a' }}
                prefix={<ExclamationCircleOutlined />}
              />
              <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
                嚴重: {stats.criticalCount} 個
              </div>
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <div style={{ textAlign: 'center' }}>
                <Gauge
                  percent={stats.avgNoise / 100}
                  range={{
                    color: stats.avgNoise > 70 ? '#f5222d' : stats.avgNoise > 55 ? '#fa8c16' : '#52c41a'
                  }}
                  indicator={{
                    pointer: {
                      style: {
                        stroke: '#D0D0D0',
                      },
                    },
                    pin: {
                      style: {
                        stroke: '#D0D0D0',
                      },
                    },
                  }}
                  statistic={{
                    content: {
                      style: {
                        fontSize: '16px',
                        lineHeight: '16px',
                      },
                      formatter: () => `${stats.avgNoise.toFixed(1)} dB`,
                    },
                  }}
                  height={120}
                />
              </div>
            </Card>
          </Col>
        </Row>
      )}

      {/* 噪音趨勢圖表 */}
      <Card title="噪音趨勢分析" style={{ marginBottom: 16 }}>
        {prepareChartData().length > 0 ? (
          <Line
            data={prepareChartData().flatMap(item => [
              { time: item.time, value: item.current, category: '當前噪音', location: item.location },
              { time: item.time, value: item.average, category: '平均噪音', location: item.location },
              { time: item.time, value: item.peak, category: '峰值噪音', location: item.location }
            ])}
            xField="time"
            yField="value"
            seriesField="category"
            smooth={true}
            color={['#1890ff', '#52c41a', '#f5222d']}
            height={300}
            yAxis={{
              title: {
                text: '噪音等級 (dB)',
              },
            }}
            legend={{
              position: 'top',
            }}
            tooltip={{
              formatter: (datum: any) => ({
                name: datum.category,
                value: `${datum.value} dB (${datum.location})`,
              }),
            }}
          />
        ) : (
          <Empty description="暫無數據" />
        )}
      </Card>

      {/* 感測器詳細數據 */}
      <Card title="噪音感測器監測">
        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="_id"
          loading={loading}
          pagination={{
            total: filteredData.length,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 項，共 ${total} 項`
          }}
          locale={{
            emptyText: <Empty description="暫無噪音監測數據" />
          }}
        />
      </Card>

      {/* 閾值設置模態框 */}
      <Modal
        title="設置噪音警報閾值"
        open={thresholdModalVisible}
        onCancel={() => {
          setThresholdModalVisible(false);
          setSelectedSensor(null);
          form.resetFields();
        }}
        footer={null}
        width={500}
      >
        {selectedSensor && (
          <div>
            <div style={{ marginBottom: 16, padding: 16, background: '#f5f5f5', borderRadius: 6 }}>
              <p><strong>感測器ID:</strong> {selectedSensor.sensorId}</p>
              <p><strong>位置:</strong> {selectedSensor.location.area} - {selectedSensor.location.floor}F</p>
              <p><strong>當前噪音:</strong> {selectedSensor.measurements.decibel.current} dB</p>
              <p><strong>狀態:</strong> {getSeverityTag(selectedSensor.classification.severity)}</p>
            </div>

            <Form
              form={form}
              layout="vertical"
              onFinish={updateThresholds}
            >
              <Form.Item
                name="warning"
                label="警告閾值 (dB)"
                rules={[
                  { required: true, message: '請輸入警告閾值' },
                  { type: 'number', min: 30, max: 100, message: '閾值範圍: 30-100 dB' }
                ]}
              >
                <InputNumber
                  min={30}
                  max={100}
                  style={{ width: '100%' }}
                  placeholder="建議設置為 55-70 dB"
                />
              </Form.Item>

              <Form.Item
                name="critical"
                label="嚴重閾值 (dB)"
                rules={[
                  { required: true, message: '請輸入嚴重閾值' },
                  { type: 'number', min: 40, max: 120, message: '閾值範圍: 40-120 dB' }
                ]}
              >
                <InputNumber
                  min={40}
                  max={120}
                  style={{ width: '100%' }}
                  placeholder="建議設置為 70-85 dB"
                />
              </Form.Item>

              <div style={{ marginBottom: 16, padding: 12, background: '#e6f7ff', borderRadius: 4 }}>
                <h4 style={{ margin: '0 0 8px 0', color: '#1890ff' }}>噪音等級參考:</h4>
                <div style={{ fontSize: 12, lineHeight: '18px' }}>
                  <div>• 30-40 dB: 安靜環境 (圖書館)</div>
                  <div>• 40-55 dB: 正常環境 (辦公室)</div>
                  <div>• 55-70 dB: 吵雜環境 (餐廳)</div>
                  <div>• 70-85 dB: 很吵環境 (交通要道)</div>
                  <div>• 85+ dB: 危險等級 (需防護)</div>
                </div>
              </div>

              <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                <Space>
                  <Button onClick={() => {
                    setThresholdModalVisible(false);
                    setSelectedSensor(null);
                    form.resetFields();
                  }}>
                    取消
                  </Button>
                  <Button type="primary" htmlType="submit">
                    保存設置
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default NoiseMonitoring;